import { MessagesModule } from 'primeng/messages';
import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { VehicleProfile } from '../../models/common-data-types';
import { ShowDocumentsComponent } from '../common/show-documents/show-documents.component';
import { ToastModule } from 'primeng/toast';
import { Message, MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { VehiclesService } from '../../service/vehicles.service';
import { ProgressLoaderService } from '../../service/progress-loader.service';
import { TableModule } from 'primeng/table';
import { DocumentsService } from '../../service/documents.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { VerifyCustomerKycDocumentComponent } from '../common/verify-customer-kyc-document/verify-customer-kyc-document.component';
import { ProgressBarModule } from 'primeng/progressbar';
import { TooltipModule } from 'primeng/tooltip';
import { DocumentEntities } from '../../constants/document-entities';
import { languages } from '../../constants/languages';
import { LanguageService } from '../../service/language.service';
import { NgxTranslateModule } from 'src/app/translate.module';
import { TranslateService } from '@ngx-translate/core';
import { CustomerProfileDocumentViewComponent } from '../common/customer-profile-document-view/customer-profile-document-view.component';
import { showErrorToast } from '../../utilities/toast-utils';

@Component({
    selector: 'app-vehicle-detail',
    standalone: true,
    imports: [
        CommonModule,
        ButtonModule,
        CardModule,
        ProgressBarModule,
        NgxTranslateModule,
        MessagesModule,
        TooltipModule,
        ToastModule,
        TableModule,
    ],
    templateUrl: './vehicle-detail.component.html',
    styleUrl: './vehicle-detail.component.scss',
    providers: [MessageService],
})
export class VehicleDetailComponent {
    vehicle!: VehicleProfile;
    messages: Message[] = [];
    vehicleId!: string;
    blobUrl: SafeResourceUrl | null = null;
    ref: DynamicDialogRef | undefined;

    languages: any[] = languages;
    selectedLanguage!: string;

    loadingDocumentView: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private dialogService: DialogService,
        private vehiclesService: VehiclesService,
        private progressLoader: ProgressLoaderService,
        private router: Router,
        private messageService: MessageService,
        private documentsService: DocumentsService,
        private sanitizer: DomSanitizer,
        private languageService: LanguageService,
        private translateService: TranslateService
    ) {}

    ngOnInit() {
        this.extractIds();
    }

    extractIds() {
        this.route.paramMap.subscribe((params) => {
            const id = params.get('id');
            if (id) {
                this.vehicleId = id;
                this.getVehicleProfile();
            }
        });
        this.initializeLocale();
    }

    initializeLocale() {
        this.languageService.currentLanguage$.subscribe((language) => {
            this.selectedLanguage = language;
            this.setLanguage(this.selectedLanguage);
        });
    }

    setLanguage(language: string) {
        this.translateService.use(language);
        this.languageService.setLanguage(language);
    }

    getVehicleProfile() {
        this.progressLoader.show();
        this.vehiclesService.getVehicleProfile(this.vehicleId).subscribe({
            next: (value) => {
                this.vehicle = value;
                this.progressLoader.hide();
            },
            error: (err) => {
                showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService,this.translateService)
                this.progressLoader.hide();
            },
        });
    }

    getKycDocument(id: string) {
        this.loadingDocumentView = true;
        this.documentsService.getDocumentById(id).subscribe((res) => {
            this.loadingDocumentView = false;
            const blobUrl = URL.createObjectURL(this.getBlobItem(res));
            this.blobUrl =
                this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
            this.viewVehicleDocument(res.body.type, id);
        });
    }

    goToCustomerDetails() {
        this.router.navigate([
            '/home/customers/' + this.vehicle.vehicle.owner.id,
        ]);
    }

    getBlobItem(res: any): Blob {
        if (res.body.type === 'application/pdf') {
            return new Blob([res.body], {
                type: 'application/pdf',
            });
        } else if (res.body.type === 'image/png') {
            return new Blob([res.body], { type: 'image/png' });
        } else if (res.body.type === 'image/jpeg') {
            return new Blob([res.body], { type: 'image/jpeg' });
        } else {
            return new Blob([res.body], {
                type: 'application/octet-stream',
            });
        }
    }

    viewVehicleDocument(mimeType: string, documentId: string) {
        const verificationData = {
            blobUrl: this.blobUrl,
            mimeType: mimeType,
            documentId: documentId,
            entityId: this.vehicleId,
            entity: DocumentEntities.VEHICLE,
            customerId: this.vehicle.vehicle.owner.id,
        };
        this.ref = this.dialogService.open(VerifyCustomerKycDocumentComponent, {
            width: '85%',
            showHeader: false,
            contentStyle: { overflow: 'scroll' },
            baseZIndex: 10000,
            maximizable: false,
            data: verificationData,
        });

        this.ref.onClose.subscribe((result) => {
            this.getVehicleProfile();
        });
    }

    openVerifiedDocument(documentId: string, mimeType: string) {
        this.loadingDocumentView = true;
        this.documentsService.getDocumentById(documentId).subscribe((res) => {
            this.loadingDocumentView = false;
            const blobUrl = URL.createObjectURL(this.getBlobItem(res));
            this.blobUrl =
                this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);

            const viewData = {
                blobUrl: blobUrl,
                mimeType: mimeType,
                documentId: documentId,
                title: this.translateService.instant("Vehicle Document")
            };

            this.ref = this.dialogService.open(
                CustomerProfileDocumentViewComponent,
                {
                    width: '85%',
                    showHeader: false,
                    contentStyle: { overflow: 'scroll' },
                    baseZIndex: 10000,
                    maximizable: false,
                    data: viewData,
                }
            );
        });
    }
}
